import {
  sanitizeHtml,
  companyName as companyNameOf,
  safeHref,
  textRows,
} from "@1-now/booking-core";
import { buildContactRows, companyContactRows, ContactRow } from "utils/contactRows";
import { footerHours } from "utils/footerHours";
import { formatPhone } from "utils/formatPhone";
import Logo from "designs/DesignB/components/Logo/Logo";
import DbIcon, { IconName } from "designs/DesignB/components/icons";
import { FooterLink } from "data/types";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useData } from "data/data-provider";
import { useHiddenSectionLink } from "utils/useHiddenSectionLink";

export interface WidgetFooterMenu {
  id: string;
  title: string;
  menus: FooterLink[];
}

// Label to route, explicit: a label-derived slug sent "About Us" to /about-us (404).
const DEFAULT_LINKS = [
  { label: "About Us", path: "/about" },
  { label: "Vehicles", path: "/vehicles" },
  { label: "Contact", path: "/contact" },
];

const isAbsoluteUrl = (s?: string) => !!s && /^https?:\/\//i.test(s);
const isMailOrTel = (s?: string) =>
  !!s && (s.startsWith("mailto:") || s.startsWith("tel:"));

// A social without a glyph of its own gets a line icon from Design B's set, picked by what the
// link says (1NOW-11175); one the tenant uploaded a glyph for keeps it.
const socialIcon = (label: string, href: string): IconName => {
  const key = `${label} ${href}`.toLowerCase();
  if (/insta/.test(key)) return "instagram";
  if (/facebook|fb\.com|fb\.me/.test(key)) return "facebook";
  if (/twitter|x\.com/.test(key)) return "x";
  if (/youtu/.test(key)) return "youtube";
  return "globe";
};

const LINK_CLASS = "db-footer__link";

// A phone typed as a bare number ("14109959944") is shown the way the rest of the site shows a
// phone; a row that says anything else is left as the operator wrote it (1NOW-11175).
const PHONE_ONLY = /^\+?[\d\s().-]{7,}$/;
const shown = (row: ContactRow) =>
  row.type === "phone" && PHONE_ONLY.test(row.display.trim()) ? formatPhone(row.display) : row.display;

/** The caption over a contact row: the operator's own label, else what kind of row it is. */
const rowCaption = (row: ContactRow) =>
  row.label || (row.type === "phone" ? "Call us" : row.type === "whatsapp" ? "WhatsApp" : row.type === "email" ? "Email" : "");

const mapsHref = (address: string) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const {
    companyDetails,
    contactItems: rawContactItems,
    footerItems: rawFooterItems,
    address: rawAddress,
    socialItems: rawSocialItems,
    footer,
    ourLocation,
    ourCompany,
  } = useData();
  // The operator's rows, read the package's way: text only — a description
  // that arrives as an object no longer takes the whole site down — and
  // contact values shown without a stored scheme, with `href` to dial them.
  // The operator's contact rows, else the company's own phone and email, as the Contact page
  // does (1NOW-11109).
  const ownContactRows = buildContactRows(rawContactItems);
  const contactItems = ownContactRows.length > 0 ? ownContactRows : companyContactRows(companyDetails);
  const footerItems = textRows(rawFooterItems);
  // One address: the FMS keypoints when there are any, else the company's own.
  const addressKeypoints = textRows(rawAddress).filter((a: any) => a.description.trim());
  const companyAddress = String(companyDetails?.address ?? "").trim();
  const address =
    addressKeypoints.length > 0
      ? addressKeypoints
      : companyAddress
      ? [{ id: "company-address", description: companyAddress }]
      : [];
  const socialItems = textRows(rawSocialItems);
  // The branch's hours, only when there is one branch that states them (footerHours).
  const hours = footerHours(ourLocation, ourCompany);

  // Convention used by the website-builder: footerItems with a `heading` are
  // labelled links; ones without are plain info lines under the brand block.
  // A link to a home page section this design does not draw is left out (1NOW-11111).
  const hiddenLink = useHiddenSectionLink();
  const links = (footerItems || []).filter((item: any) => item?.heading && !hiddenLink(item.description));
  const infoLines = (footerItems || []).filter((item: any) => !item?.heading);

  const currentYear = new Date().getFullYear();
  const companyName: string = companyNameOf(companyDetails);

  // 1NOW layout variant chosen in the FMS (Section.layout_variant):
  //   "columns"  → brand + links + contact (default)
  //   "centered" → minimal centered single column: logo + socials + tagline
  const layoutVariant: "columns" | "centered" =
    footer?.layout_variant === "centered" ? "centered" : "columns";

  const renderSocials = (className: string) =>
    socialItems?.length > 0 && (
      <div className={className}>
        {socialItems.map((s: any, i: number) => {
          const href = safeHref(s?.description) || "#";
          const label = s?.heading || "Social";
          return (
            <a
              key={s?.id || i}
              href={href}
              target={isMailOrTel(href) ? undefined : "_blank"}
              rel={isMailOrTel(href) ? undefined : "noopener noreferrer"}
              aria-label={label}
              title={label}
              className="db-footer__social"
            >
              {s?.image_url ? (
                <img
                  src={s.image_url}
                  /* The enclosing <a> carries the label, so the glyph is decorative (1NOW-10440). */
                  alt=""
                  aria-hidden="true"
                  className="h-5 w-5 object-contain"
                />
              ) : (
                <DbIcon name={socialIcon(label, href)} className="h-5 w-5" />
              )}
            </a>
          );
        })}
      </div>
    );

  const renderLink = (item: any, index: number) => {
    const href = item.description || "#";
    const isExternal = isAbsoluteUrl(href) || isMailOrTel(href);
    return isExternal ? (
      <a
        key={item.id || index}
        href={href}
        target={isMailOrTel(href) ? undefined : "_blank"}
        rel={isMailOrTel(href) ? undefined : "noopener noreferrer"}
        className={LINK_CLASS}
      >
        {item.heading}
      </a>
    ) : (
      <button key={item.id || index} type="button" onClick={() => navigate(href)} className={LINK_CLASS}>
        {item.heading}
      </button>
    );
  };

  /** One contact row: an icon tile, a small caption and the value; a link when it dials. */
  const renderContact = (row: ContactRow, index: number) => {
    const caption = rowCaption(row);
    return (
      <li key={row.id || index} className="db-crow">
        <span className="db-crow__icon">
          <DbIcon name={row.type === "email" ? "mail" : row.type === "text" ? "clock" : "phone"} className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          {caption && <small>{caption}</small>}
          {row.href ? (
            <a href={row.href} className="db-crow__value">
              {shown(row)}
            </a>
          ) : (
            <span className="db-crow__value">{shown(row)}</span>
          )}
        </div>
      </li>
    );
  };

  const renderAddress = (item: any, index: number) => (
    <li key={item.id || index} className="db-crow">
      <span className="db-crow__icon">
        <DbIcon name="pin" className="h-5 w-5" />
      </span>
      <div className="min-w-0">
        <small>Visit</small>
        <a
          href={mapsHref(item.description)}
          target="_blank"
          rel="noopener noreferrer"
          className="db-crow__value"
        >
          {item.description}
        </a>
      </div>
    </li>
  );

  const renderHours = () =>
    hours.length > 0 && (
      <li className="db-crow">
        <span className="db-crow__icon">
          <DbIcon name="clock" className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <small>Hours</small>
          {hours.map((line) => (
            <span key={line} className="db-crow__value">
              {line}
            </span>
          ))}
        </div>
      </li>
    );

  return (
    <footer className="db-footer bg-footer-color text-footer-text-color">
      <div className="container pt-12 pb-6 sm:pt-14">
        {layoutVariant === "columns" && (
          <div className="grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-8">
            {/* Brand block */}
            <div className="md:col-span-5">
              <div className="mb-4">
                <Logo />
              </div>
              {footer?.description && (
                <p
                  className="max-w-sm text-sm leading-relaxed opacity-70"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(footer.description) }}
                />
              )}

              {/* FMS-driven info lines (e.g. tagline, license, address note) */}
              {infoLines.length > 0 && (
                <div className="mt-3 max-w-sm space-y-1 text-sm opacity-70">
                  {infoLines.map((item: any, index: number) => (
                    <p key={item.id || index} className="break-words leading-relaxed">
                      {item.description}
                    </p>
                  ))}
                </div>
              )}

              {renderSocials("mt-6 flex flex-wrap items-center gap-2.5")}
            </div>

            {/* Quick Links */}
            <div className="md:col-span-3">
              <h3 className="db-footer__h">Explore</h3>
              <ul className="db-footer__list">
                {links.length > 0
                  ? links.map((item: any, index: number) => <li key={item.id || index}>{renderLink(item, index)}</li>)
                  : DEFAULT_LINKS.map(({ label, path }) => (
                      <li key={path}>
                        <Link to={path} className={LINK_CLASS}>
                          {label}
                        </Link>
                      </li>
                    ))}
              </ul>
            </div>

            {/* Contact */}
            <div className="md:col-span-4">
              {(contactItems.length > 0 || address.length > 0 || hours.length > 0) && (
                <h3 className="db-footer__h">Get in Touch</h3>
              )}
              <ul className="db-footer__list db-footer__list--rows">
                {contactItems.map(renderContact)}
                {address.map(renderAddress)}
                {renderHours()}
              </ul>
            </div>
          </div>
        )}

        {/* Centered footer — logo + tagline + horizontal nav row + inline contact + address +
            socials, all centered: the structural grid is replaced by stacked centered rows but
            the information density is preserved. */}
        {layoutVariant === "centered" && (
          <div className="flex flex-col items-center gap-5 text-center">
            <Logo />

            {/* Brand tagline — FMS-driven info lines, else the footer's editable description. */}
            {infoLines.length > 0 ? (
              <div className="max-w-xl space-y-1 text-sm leading-relaxed opacity-70">
                {infoLines.map((item: any, index: number) => (
                  <p key={item.id || index}>{item.description}</p>
                ))}
              </div>
            ) : footer?.description ? (
              <p
                className="max-w-xl text-sm leading-relaxed opacity-70"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(footer.description) }}
              />
            ) : null}

            {links.length > 0 && (
              <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-1">
                {links.map(renderLink)}
              </nav>
            )}

            {(contactItems.length > 0 || address.length > 0 || hours.length > 0) && (
              <ul className="flex flex-wrap items-start justify-center gap-x-8 gap-y-4 text-left">
                {contactItems.map(renderContact)}
                {address.map(renderAddress)}
                {renderHours()}
              </ul>
            )}

            {renderSocials("flex flex-wrap items-center justify-center gap-2.5")}
          </div>
        )}

        {/* 1NOW-10372: legal links are not FMS-driven — every tenant site has to expose a
            privacy policy and terms URL (SMS carriers require one before approving a
            campaign), so this bottom bar renders on both layout variants regardless of how the
            footer is configured. Copyright left, links right on the columns layout; both
            centered on the centered layout. */}
        <div className="db-footer__bottom mt-10 pt-5 text-[13px] sm:mt-12">
          <div
            className={`flex flex-col-reverse items-center gap-3 ${
              layoutVariant === "centered" ? "" : "sm:flex-row sm:justify-between"
            }`}
          >
            {companyName && (
              <p className="opacity-70">
                © {currentYear} {companyName}. All rights reserved.
              </p>
            )}
            <div
              className={`flex flex-wrap items-center justify-center gap-x-6 gap-y-1 ${
                layoutVariant === "centered" ? "" : "sm:ml-auto"
              }`}
            >
              <Link to="/privacy-policy" className={LINK_CLASS}>
                Privacy Policy
              </Link>
              <Link to="/terms-and-conditions" className={LINK_CLASS}>
                Terms &amp; Conditions
              </Link>
              {/* 1NOW-11157 */}
              <Link to="/track-booking" className={LINK_CLASS}>
                Track my booking
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
